import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchNavItems,
  updateNavItem,
  bulkUpdateNavOrder,
  invalidateNavCache,
  FALLBACK_NAV,
  type NavItem,
} from '../../lib/nav-client';

// ── Auth ──────────────────────────────────────────────────────────────────────

const API_BASE = '';

async function checkAuth(password: string): Promise<string | null> {
  const res = await fetch(`${API_BASE}/api/cms/auth`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) return null;
  const { token } = await res.json();
  return token as string | null;
}

function useAdminAuth() {
  const [authed, setAuthed] = useState(() => !!sessionStorage.getItem('gfs_admin_token'));
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const login = async () => {
    setLoading(true); setErr('');
    const token = await checkAuth(pw);
    if (token) { sessionStorage.setItem('gfs_admin_token', token); setAuthed(true); }
    else setErr('Incorrect password');
    setLoading(false);
  };

  return { authed, pw, setPw, err, loading, login };
}

// ── Header preview ────────────────────────────────────────────────────────────

function HeaderPreview({ items }: { items: NavItem[] }) {
  const headerItems = items.filter(i => i.active && i.header_visible && !i.is_cta).sort((a, b) => a.sort_order - b.sort_order);
  const moreItems   = items.filter(i => i.active && i.more_visible).sort((a, b) => a.sort_order - b.sort_order);
  const cta         = items.find(i => i.active && i.is_cta);

  return (
    <div className="bg-[#0D0D0D] border border-gray-700 rounded-xl px-5 py-3 flex items-center gap-6 overflow-x-auto mb-8">
      <div className="w-8 h-8 bg-gray-800 rounded flex-shrink-0 flex items-center justify-center">
        <span className="text-[10px] text-gray-500">Logo</span>
      </div>
      <div className="flex items-center gap-5 flex-1">
        {headerItems.map(item => (
          <span key={item.id} className="text-sm text-[#E0E0E0] whitespace-nowrap">{item.label}</span>
        ))}
        {moreItems.length > 0 && (
          <span className="text-sm text-[#E0E0E0] flex items-center gap-0.5 whitespace-nowrap">
            More <span className="text-gray-500">▾</span>
          </span>
        )}
      </div>
      {cta && (
        <span className="bg-[#39FF14] text-[#0D0D0D] text-xs font-bold px-3 py-1.5 rounded-lg whitespace-nowrap flex-shrink-0">
          {cta.label}
        </span>
      )}
    </div>
  );
}

// ── Toggle switch ─────────────────────────────────────────────────────────────

function Toggle({ value, onChange, disabled }: { value: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      onClick={() => !disabled && onChange(!value)}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
        value ? 'bg-[#39FF14]' : 'bg-gray-700'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${value ? 'translate-x-4' : 'translate-x-0'}`} />
    </button>
  );
}

// ── Nav item row ──────────────────────────────────────────────────────────────

const FOOTER_GROUPS = [
  { value: 'repairs', label: 'Repairs' },
  { value: 'gamefix', label: 'GameFix' },
  { value: 'support', label: 'Support' },
];

interface RowProps {
  item: NavItem;
  onUpdate: (id: string, patch: Partial<NavItem>) => void;
  onMoveUp: (id: string) => void;
  onMoveDown: (id: string) => void;
  isFirst: boolean;
  isLast: boolean;
  saving: boolean;
}

function NavItemRow({ item, onUpdate, onMoveUp, onMoveDown, isFirst, isLast, saving }: RowProps) {
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState(item.label);

  const commitLabel = () => {
    setEditingLabel(false);
    if (labelDraft.trim() && labelDraft !== item.label) {
      onUpdate(item.id, { label: labelDraft.trim() });
    }
  };

  return (
    <div className={`flex items-center gap-4 px-4 py-3 rounded-xl border transition-all ${item.active ? 'bg-gray-900 border-gray-800' : 'bg-gray-950 border-gray-800/50 opacity-60'}`}>
      {/* Reorder */}
      <div className="flex flex-col gap-0.5 flex-shrink-0">
        <button onClick={() => onMoveUp(item.id)} disabled={isFirst || saving}
          className="text-gray-600 hover:text-gray-300 disabled:opacity-20 transition-colors text-xs leading-none px-1">▲</button>
        <button onClick={() => onMoveDown(item.id)} disabled={isLast || saving}
          className="text-gray-600 hover:text-gray-300 disabled:opacity-20 transition-colors text-xs leading-none px-1">▼</button>
      </div>

      {/* Label */}
      <div className="w-44 flex-shrink-0">
        {editingLabel ? (
          <input
            autoFocus
            value={labelDraft}
            onChange={e => setLabelDraft(e.target.value)}
            onBlur={commitLabel}
            onKeyDown={e => { if (e.key === 'Enter') commitLabel(); if (e.key === 'Escape') { setEditingLabel(false); setLabelDraft(item.label); } }}
            className="bg-gray-800 border border-[#39FF14]/40 text-[#E0E0E0] text-sm px-2 py-1 rounded w-full focus:outline-none"
          />
        ) : (
          <button onClick={() => setEditingLabel(true)}
            className="text-sm text-[#E0E0E0] hover:text-[#39FF14] transition-colors text-left w-full truncate font-medium"
            title="Click to rename">
            {item.label}
          </button>
        )}
        <p className="text-[10px] text-gray-600 truncate mt-0.5 font-mono">{item.route}</p>
      </div>

      {/* Toggles */}
      <div className="flex items-center gap-6">
        <div className="flex flex-col items-center gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-wide">Header</span>
          <Toggle value={item.header_visible} onChange={v => onUpdate(item.id, { header_visible: v })} disabled={saving} />
        </div>
        <div className="flex flex-col items-center gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-wide">More</span>
          <Toggle value={item.more_visible} onChange={v => onUpdate(item.id, { more_visible: v })} disabled={saving || !!item.is_cta} />
        </div>
        <div className="flex flex-col items-center gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-wide">Footer</span>
          <Toggle value={item.footer_visible} onChange={v => onUpdate(item.id, { footer_visible: v })} disabled={saving} />
        </div>
      </div>

      {/* Footer group */}
      <div className="flex flex-col gap-1 w-28 flex-shrink-0">
        <span className="text-[10px] text-gray-500 uppercase tracking-wide">Footer group</span>
        <select
          value={item.footer_group ?? ''}
          onChange={e => onUpdate(item.id, { footer_group: e.target.value || null })}
          disabled={saving}
          className="bg-gray-800 border border-gray-700 rounded-lg text-xs text-[#E0E0E0] px-2 py-1 focus:outline-none focus:border-gray-500"
        >
          <option value="">— none —</option>
          {FOOTER_GROUPS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
        </select>
      </div>

      {/* Active toggle */}
      <div className="ml-auto flex flex-col items-center gap-1 flex-shrink-0">
        <span className="text-[10px] text-gray-500 uppercase tracking-wide">Active</span>
        <Toggle value={item.active} onChange={v => onUpdate(item.id, { active: v })} disabled={saving} />
      </div>

      {/* CTA badge */}
      {item.is_cta && (
        <span className="text-[10px] font-bold px-2 py-0.5 bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/30 rounded-full flex-shrink-0">CTA</span>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

function NavManagerContent() {
  const [items, setItems] = useState<NavItem[]>(FALLBACK_NAV);
  const [saving, setSaving] = useState<string | null>(null); // item id currently saving
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(() => {
    invalidateNavCache();
    fetchNavItems().then(setItems).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpdate = useCallback(async (id: string, patch: Partial<NavItem>) => {
    // Optimistic update
    setItems(prev => prev.map(i => i.id === id ? { ...i, ...patch } : i));
    setSaving(id); setSaveError(null);
    try {
      await updateNavItem(id, patch);
    } catch (e) {
      setSaveError((e as Error).message);
      load(); // revert
    } finally {
      setSaving(null);
    }
  }, [load]);

  const handleMoveUp = useCallback(async (id: string) => {
    setItems(prev => {
      const idx = prev.findIndex(i => i.id === id);
      if (idx <= 0) return prev;
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next.map((item, i) => ({ ...item, sort_order: (i + 1) * 10 }));
    });
    setSaving(id); setSaveError(null);
    try {
      const updated = items.map((item, i) => ({ ...item, sort_order: (i + 1) * 10 }));
      const idx = updated.findIndex(i => i.id === id);
      if (idx <= 0) return;
      const newOrder = [...updated];
      [newOrder[idx - 1], newOrder[idx]] = [newOrder[idx], newOrder[idx - 1]];
      const reindexed = newOrder.map((item, i) => ({ id: item.id, sort_order: (i + 1) * 10 }));
      await bulkUpdateNavOrder(reindexed);
      load();
    } catch (e) { setSaveError((e as Error).message); load(); }
    finally { setSaving(null); }
  }, [items, load]);

  const handleMoveDown = useCallback(async (id: string) => {
    setSaving(id); setSaveError(null);
    try {
      const idx = items.findIndex(i => i.id === id);
      if (idx < 0 || idx >= items.length - 1) return;
      const newOrder = [...items];
      [newOrder[idx], newOrder[idx + 1]] = [newOrder[idx + 1], newOrder[idx]];
      const reindexed = newOrder.map((item, i) => ({ id: item.id, sort_order: (i + 1) * 10 }));
      await bulkUpdateNavOrder(reindexed);
      load();
    } catch (e) { setSaveError((e as Error).message); load(); }
    finally { setSaving(null); }
  }, [items, load]);

  // Group items for display
  const sorted = [...items].sort((a, b) => a.sort_order - b.sort_order);
  const headerItems   = sorted.filter(i => i.header_visible || i.is_cta);
  const moreItems     = sorted.filter(i => !i.header_visible && !i.is_cta && i.more_visible);
  const footerOnly    = sorted.filter(i => !i.header_visible && !i.is_cta && !i.more_visible && i.footer_visible);
  const hiddenItems   = sorted.filter(i => !i.header_visible && !i.is_cta && !i.more_visible && !i.footer_visible);

  const renderGroup = (title: string, groupItems: NavItem[], accent: string) => {
    if (groupItems.length === 0) return null;
    return (
      <section className="mb-8">
        <h3 className={`text-xs font-bold uppercase tracking-widest mb-3 ${accent}`}>{title}</h3>
        <div className="space-y-2">
          {groupItems.map((item, idx) => (
            <NavItemRow
              key={item.id}
              item={item}
              onUpdate={handleUpdate}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
              isFirst={idx === 0}
              isLast={idx === groupItems.length - 1}
              saving={saving === item.id}
            />
          ))}
        </div>
      </section>
    );
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-[#E0E0E0] pb-20">
      <div className="max-w-4xl mx-auto px-6 py-10">

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-black text-white">Navigation Manager</h1>
            <p className="text-gray-500 text-sm mt-1">Control what appears in the header, More menu, and footer. Changes are live immediately.</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} className="text-xs text-gray-500 hover:text-gray-300 transition-colors border border-gray-700 px-3 py-1.5 rounded-lg">
              Refresh
            </button>
            <Link to="/admin/services" className="text-xs text-gray-500 hover:text-gray-300 transition-colors border border-gray-700 px-3 py-1.5 rounded-lg">
              ← Admin Home
            </Link>
          </div>
        </div>

        {saveError && (
          <div className="mb-6 px-4 py-3 bg-red-900/30 border border-red-700/50 rounded-xl text-sm text-red-400">
            Save failed: {saveError}
          </div>
        )}

        {/* Live header preview */}
        <div className="mb-8">
          <p className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-2">Live Header Preview</p>
          <HeaderPreview items={items} />
        </div>

        {/* Legend */}
        <div className="flex items-center gap-6 text-xs text-gray-600 mb-8 px-1">
          <span>▲▼ Reorder</span>
          <span>Click label to rename</span>
          <span>Header = main nav bar</span>
          <span>More = dropdown</span>
          <span>Footer = footer columns</span>
        </div>

        {/* Item groups */}
        {renderGroup('Header Navigation', headerItems, 'text-[#39FF14]')}
        {renderGroup('More Dropdown', moreItems, 'text-[#00BFFF]')}
        {renderGroup('Footer Only', footerOnly, 'text-gray-400')}
        {hiddenItems.length > 0 && renderGroup('Hidden / Inactive', hiddenItems, 'text-gray-600')}

        {/* Footer group column management */}
        <section className="mb-8">
          <h3 className="text-xs font-bold uppercase tracking-widest mb-3 text-amber-500">Footer Group Ordering</h3>
          <p className="text-sm text-gray-500 mb-4">
            Assign items to footer groups above. Group order: <strong className="text-gray-400">Repairs (1) → GameFix (2) → Support (3)</strong>.
            To reorder groups, change the group assignment of items so lighter-numbered groups appear first.
          </p>
          <div className="grid grid-cols-3 gap-4">
            {['repairs', 'gamefix', 'support'].map(gKey => {
              const gLabel = gKey.charAt(0).toUpperCase() + gKey.slice(1) + (gKey === 'gamefix' ? '' : '');
              const gItems = items.filter(i => i.footer_group === gKey && i.footer_visible && i.active)
                .sort((a, b) => a.footer_sort_order - b.footer_sort_order);
              return (
                <div key={gKey} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">
                    {gKey === 'gamefix' ? 'GameFix' : gKey === 'repairs' ? 'Repairs' : 'Support'}
                  </p>
                  {gItems.length === 0
                    ? <p className="text-xs text-gray-700">No items</p>
                    : gItems.map(i => <p key={i.id} className="text-xs text-gray-400 py-0.5">{i.label}</p>)
                  }
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}

// ── Auth gate ─────────────────────────────────────────────────────────────────

export default function AdminNavigationPage() {
  const { authed, pw, setPw, err, loading, login } = useAdminAuth();

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 w-full max-w-sm">
          <h1 className="text-xl font-black text-white mb-6">Admin Login</h1>
          <input
            type="password"
            value={pw}
            onChange={e => setPw(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && login()}
            placeholder="Password"
            className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-[#E0E0E0] placeholder-gray-600 focus:outline-none focus:border-[#39FF14]/50 mb-4"
          />
          {err && <p className="text-red-400 text-sm mb-4">{err}</p>}
          <button
            onClick={login}
            disabled={loading}
            className="w-full bg-[#39FF14] hover:bg-[#00BFFF] text-[#0D0D0D] font-bold py-3 rounded-xl transition-colors disabled:opacity-50"
          >
            {loading ? 'Checking…' : 'Log In'}
          </button>
        </div>
      </div>
    );
  }

  return <NavManagerContent />;
}
